export interface BillingAdminAuthConfig {
  issuer?: string;
  clientId?: string;
  redirectUri?: string;
  postLogoutUri?: string;
}

export interface ResolvedBillingAdminAuthConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  postLogoutUri: string;
}

export interface BillingAdminSession {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt?: number;
}

interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
}

interface PkceState {
  verifier: string;
  state: string;
  nonce: string;
  returnTo: string;
}

const SESSION_KEY = 'billing_admin_auth_session';
const TOKEN_KEY = 'billing_admin_token';
const PKCE_KEY = 'billing_admin_pkce';
const POST_LOGOUT_KEY = 'billing_admin_post_logout';
const TOKEN_QUERY_PARAM = 'token';
const DEFAULT_SCOPE = 'openid profile email org permissions offline_access';

let cachedOidcConfig: { issuer: string; value: OidcConfig } | null = null;

interface OidcConfig {
  authorization_endpoint: string;
  token_endpoint: string;
  end_session_endpoint?: string;
}

function persistPkceState(pkceState: PkceState): void {
  const serialized = JSON.stringify(pkceState);
  sessionStorage.setItem(PKCE_KEY, serialized);
  localStorage.setItem(PKCE_KEY, serialized);
}

function readPkceState(): PkceState | null {
  const raw = sessionStorage.getItem(PKCE_KEY) ?? localStorage.getItem(PKCE_KEY);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as PkceState;
  } catch {
    clearPkceState();
    return null;
  }
}

function clearPkceState(): void {
  sessionStorage.removeItem(PKCE_KEY);
  localStorage.removeItem(PKCE_KEY);
}

function randomBase64Url(byteLength: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

async function sha256Base64Url(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

export function resolveBillingAdminAuthConfig(
  basename = '/admin/billing',
  authConfig?: BillingAdminAuthConfig,
): ResolvedBillingAdminAuthConfig {
  const normalizedBase = basename.startsWith('/') ? basename : `/${basename}`;
  return {
    issuer: authConfig?.issuer ?? 'https://auth.rule42.ai',
    clientId: authConfig?.clientId ?? 'rule42-billing-web',
    redirectUri: authConfig?.redirectUri ?? `${window.location.origin}${normalizedBase}/auth/callback`,
    postLogoutUri: authConfig?.postLogoutUri ?? `${window.location.origin}${normalizedBase}/login`,
  };
}

export function getBootstrapToken(): string | null {
  const searchParams = new URLSearchParams(window.location.search);
  const searchToken = searchParams.get(TOKEN_QUERY_PARAM);
  if (searchToken) {
    return searchToken;
  }

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  return hashParams.get(TOKEN_QUERY_PARAM);
}

export function clearBootstrapTokenFromUrl(): void {
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);

  const hadSearchToken = searchParams.has(TOKEN_QUERY_PARAM);
  const hadHashToken = hashParams.has(TOKEN_QUERY_PARAM);
  searchParams.delete(TOKEN_QUERY_PARAM);
  hashParams.delete(TOKEN_QUERY_PARAM);
  if (!hadSearchToken && !hadHashToken) {
    return;
  }

  const query = searchParams.toString();
  const nextHash = hashParams.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${nextHash ? `#${nextHash}` : ''}`;
  window.history.replaceState({}, document.title, nextUrl);
}

export function readStoredSession(): BillingAdminSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as BillingAdminSession;
    if (!parsed.accessToken) {
      clearAuthSession();
      return null;
    }
    if (parsed.expiresAt && Date.now() / 1000 >= parsed.expiresAt) {
      clearAuthSession();
      return null;
    }
    return parsed;
  } catch {
    clearAuthSession();
    return null;
  }
}

export function persistSession(session: BillingAdminSession): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  sessionStorage.setItem(TOKEN_KEY, session.accessToken);
}

function clearAuthSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function clearStoredSession(): void {
  clearAuthSession();
  clearPkceState();
}

export function isPostLogout(): boolean {
  return sessionStorage.getItem(POST_LOGOUT_KEY) === '1';
}

export function clearPostLogout(): void {
  sessionStorage.removeItem(POST_LOGOUT_KEY);
}

async function getOidcConfig(config: ResolvedBillingAdminAuthConfig): Promise<OidcConfig> {
  if (cachedOidcConfig?.issuer === config.issuer) {
    return cachedOidcConfig.value;
  }
  const response = await fetch(`${config.issuer}/.well-known/openid-configuration`);
  if (!response.ok) {
    throw new Error('Failed to fetch OIDC discovery document');
  }
  const value = (await response.json()) as OidcConfig;
  cachedOidcConfig = { issuer: config.issuer, value };
  return value;
}

export async function startOidcLogin(
  config: ResolvedBillingAdminAuthConfig,
  returnTo = '/overview',
  opts?: { prompt?: string },
): Promise<void> {
  const { authorization_endpoint } = await getOidcConfig(config);
  const verifier = randomBase64Url(64);
  const challenge = await sha256Base64Url(verifier);
  const state = randomBase64Url(32);
  const nonce = randomBase64Url(32);

  const pkceState: PkceState = { verifier, state, nonce, returnTo };
  persistPkceState(pkceState);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: DEFAULT_SCOPE,
    state,
    nonce,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (opts?.prompt) {
    params.set('prompt', opts.prompt);
  }

  clearPostLogout();
  window.location.href = `${authorization_endpoint}?${params.toString()}`;
}

export async function handleOidcCallback(
  config: ResolvedBillingAdminAuthConfig,
): Promise<{ session: BillingAdminSession; returnTo: string }> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) {
    throw new Error(`Authorization error: ${error} — ${params.get('error_description') ?? ''}`);
  }
  if (!code || !state) {
    throw new Error('Missing code or state in callback parameters');
  }

  const pkceState = readPkceState();
  if (!pkceState) {
    throw new Error('No PKCE state found — please restart the login flow');
  }

  if (pkceState.state !== state) {
    throw new Error('State mismatch — possible CSRF attack');
  }

  const { token_endpoint } = await getOidcConfig(config);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code,
    code_verifier: pkceState.verifier,
  });

  const response = await fetch(token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Token exchange failed (${response.status}): ${detail}`);
  }

  const tokens = (await response.json()) as TokenResponse;
  const session: BillingAdminSession = {
    accessToken: tokens.access_token,
    idToken: tokens.id_token,
    refreshToken: tokens.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + tokens.expires_in,
  };
  persistSession(session);
  clearPkceState();
  clearPostLogout();
  return { session, returnTo: pkceState.returnTo };
}

export async function logoutFromOidc(
  config: ResolvedBillingAdminAuthConfig,
  session: BillingAdminSession | null,
): Promise<void> {
  clearStoredSession();
  sessionStorage.setItem(POST_LOGOUT_KEY, '1');

  try {
    const { end_session_endpoint } = await getOidcConfig(config);
    if (end_session_endpoint) {
      const params = new URLSearchParams({
        post_logout_redirect_uri: config.postLogoutUri,
        ...(session?.idToken ? { id_token_hint: session.idToken } : {}),
      });
      window.location.href = `${end_session_endpoint}?${params.toString()}`;
      return;
    }
  } catch {
    // Fall through to the local login page.
  }

  window.location.href = config.postLogoutUri;
}